import { Card, CardContent, CardHeader, CardTitle } from '@agentic-flow-studio/shared/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@agentic-flow-studio/shared/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@agentic-flow-studio/shared/ui/table';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  useAnalyticsByInbox,
  useAnalyticsByUser,
  useAnalyticsOverview,
  useWorkspaceMembers,
} from '@/hooks/useClinic';

const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
] as const;

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds) || seconds < 0) return '—';
  const hours = seconds / 3600;
  if (hours < 1) return `${Math.round(seconds / 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

const chartConfig: ChartConfig = {
  avgResponseSeconds: { label: 'Avg response time', color: 'var(--chart-1)' },
};

export default function AnalyticsPage() {
  const [days, setDays] = React.useState<number>(30);
  const range = React.useMemo(() => {
    // eslint-disable-next-line react-hooks/purity
    const now = Math.floor(Date.now() / 1000);
    return { from: now - days * 86400, to: now };
  }, [days]);

  const overviewQuery = useAnalyticsOverview(range);
  const byInboxQuery = useAnalyticsByInbox(range);
  const byUserQuery = useAnalyticsByUser(range);
  const { data: members = [] } = useWorkspaceMembers();

  const overview = overviewQuery.data;
  const byUserChartData = (byUserQuery.data || []).map((u) => {
    const member = members.find((m: { id: number }) => m.id === u.senderUserId);
    return {
      name: member?.fullName || member?.email || `User ${u.senderUserId}`,
      avgResponseSeconds: u.avgResponseSeconds ? Math.round(u.avgResponseSeconds / 60) : 0,
      replyCount: u.replyCount,
    };
  });

  return (
    <main className="flex-1 p-6 lg:p-10 space-y-8 bg-muted overflow-y-auto h-full scrollbar-thin">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="display font-display text-3xl font-light tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Response-time and resolution metrics across your workspace.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          className="text-sm bg-card border border-border/50 rounded-lg px-3 py-2 outline-none font-medium hover:border-primary/50 transition-colors "
        >
          {RANGE_OPTIONS.map((opt) => (
            <option key={opt.days} value={opt.days}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Four matching tiles made four unrelated numbers look like one urgent set.
 Hairline rows: the label carries the meaning, the number stays quiet. */}
      <ul className="border-border border-t">
        {[
          { label: 'Threads', value: overview?.threadCount ?? '—' },
          { label: 'Resolved', value: overview?.resolvedCount ?? '—' },
          {
            label: 'Average first response',
            value: formatDuration(overview?.avgFirstResponseSeconds ?? null),
          },
          {
            label: 'Average resolution',
            value: formatDuration(overview?.avgResolutionSeconds ?? null),
          },
        ].map((stat) => (
          <li key={stat.label} className="flex items-baseline gap-4 border-border border-b py-3">
            <span className="flex-1 text-[15px]">{stat.label}</span>
            <span className="tabular shrink-0 font-display text-[1.15rem] leading-none">
              {stat.value}
            </span>
          </li>
        ))}
      </ul>

      {/* Per-user response time chart */}
      <Card className="rounded-panel border-border bg-card">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Avg Response Time by User</CardTitle>
        </CardHeader>
        <CardContent>
          {byUserChartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No replies sent through the app in this range yet.
            </p>
          ) : (
            <ChartContainer config={chartConfig} className="h-64 w-full">
              <BarChart data={byUserChartData}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="name" tickLine={false} axisLine={false} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'minutes', angle: -90, position: 'insideLeft' }}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar
                  dataKey="avgResponseSeconds"
                  fill="var(--color-avgResponseSeconds)"
                  radius={4}
                />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Per-inbox table */}
        <Card className="rounded-panel border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">By Inbox</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Inbox</TableHead>
                  <TableHead>Threads</TableHead>
                  <TableHead>Resolved</TableHead>
                  <TableHead>Avg First Response</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(byInboxQuery.data || []).map((row) => (
                  <TableRow key={row.accountEmail}>
                    <TableCell className="font-medium">{row.accountEmail || 'Unknown'}</TableCell>
                    <TableCell>{row.threadCount}</TableCell>
                    <TableCell>{row.resolvedCount}</TableCell>
                    <TableCell>{formatDuration(row.avgFirstResponseSeconds)}</TableCell>
                  </TableRow>
                ))}
                {(byInboxQuery.data || []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground text-sm py-6"
                    >
                      No data for this range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Per-user table */}
        <Card className="rounded-panel border-border bg-card">
          <CardHeader>
            <CardTitle className="text-sm font-semibold">By User</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Replies Sent</TableHead>
                  <TableHead>Avg Response Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(byUserQuery.data || []).map((row) => {
                  const member = members.find((m: { id: number }) => m.id === row.senderUserId);
                  return (
                    <TableRow key={row.senderUserId}>
                      <TableCell className="font-medium">
                        {member?.fullName || member?.email || `User ${row.senderUserId}`}
                      </TableCell>
                      <TableCell>{row.replyCount}</TableCell>
                      <TableCell>{formatDuration(row.avgResponseSeconds)}</TableCell>
                    </TableRow>
                  );
                })}
                {(byUserQuery.data || []).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      className="text-center text-muted-foreground text-sm py-6"
                    >
                      No replies sent through the app in this range yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
